import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import './Widget.css';

interface CapitalAllocation {
  total: number;
  reserve: number;
  active: number;
  volume: number;
  taxAccumulated: number;
  lastUpdated: number;
}

interface CapitalGrowthProjection {
  month1: number;
  month2: number;
  month3: number;
  month6: number;
  assumptions: {
    dailyVolume: number;
    taxRate: number;
    tradingProfitRate: number;
  };
}

export const CapitalAllocation: React.FC = () => {
  const [allocation, setAllocation] = useState<CapitalAllocation | null>(null);
  const [projection, setProjection] = useState<CapitalGrowthProjection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.capital.getStatus();
        setAllocation(response.capital);
        setProjection(response.projection);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch capital data');
        console.error('Capital allocation fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="widget capital-allocation">
        <h3>Capital Allocation</h3>
        <div className="widget-loading">Loading...</div>
      </div>
    );
  }

  if (error || !allocation) {
    return (
      <div className="widget capital-allocation">
        <h3>Capital Allocation</h3>
        <div className="widget-error">{error || 'No data available'}</div>
      </div>
    );
  }

  const formatUSD = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const calculatePercentage = (amount: number): string => {
    if (allocation.total === 0) return '0%';
    return ((amount / allocation.total) * 100).toFixed(1) + '%';
  };

  return (
    <div className="widget capital-allocation">
      <h3>Capital Allocation</h3>

      <div className="capital-overview">
        <div className="total-capital">
          <label>Total Capital</label>
          <div className="value">{formatUSD(allocation.total)}</div>
        </div>

        {allocation.taxAccumulated > 0 && (
          <div className="tax-accumulated">
            <label>Tax Revenue (5%)</label>
            <div className="value tax-highlight">+{formatUSD(allocation.taxAccumulated)}</div>
          </div>
        )}
      </div>

      <div className="allocation-breakdown">
        <div className="allocation-item reserve">
          <div className="allocation-header">
            <span className="label">Reserve</span>
            <span className="percentage">{calculatePercentage(allocation.reserve)}</span>
          </div>
          <div className="allocation-bar">
            <div
              className="bar-fill reserve-fill"
              style={{ width: calculatePercentage(allocation.reserve) }}
            />
          </div>
          <div className="allocation-value">{formatUSD(allocation.reserve)}</div>
        </div>

        <div className="allocation-item active">
          <div className="allocation-header">
            <span className="label">Active Trading</span>
            <span className="percentage">{calculatePercentage(allocation.active)}</span>
          </div>
          <div className="allocation-bar">
            <div
              className="bar-fill active-fill"
              style={{ width: calculatePercentage(allocation.active) }}
            />
          </div>
          <div className="allocation-value">{formatUSD(allocation.active)}</div>
        </div>

        <div className="allocation-item volume">
          <div className="allocation-header">
            <span className="label">Volume Making</span>
            <span className="percentage">{calculatePercentage(allocation.volume)}</span>
          </div>
          <div className="allocation-bar">
            <div
              className="bar-fill volume-fill"
              style={{ width: calculatePercentage(allocation.volume) }}
            />
          </div>
          <div className="allocation-value">{formatUSD(allocation.volume)}</div>
        </div>
      </div>

      {projection && (
        <div className="capital-projection">
          <h4>Growth Projection</h4>
          <div className="projection-timeline">
            <div className="projection-item">
              <div className="month">Month 1</div>
              <div className="projected-capital">{formatUSD(projection.month1)}</div>
            </div>
            <div className="projection-item">
              <div className="month">Month 2</div>
              <div className="projected-capital">{formatUSD(projection.month2)}</div>
            </div>
            <div className="projection-item">
              <div className="month">Month 3</div>
              <div className="projected-capital">{formatUSD(projection.month3)}</div>
            </div>
            <div className="projection-item highlight">
              <div className="month">Month 6</div>
              <div className="projected-capital">{formatUSD(projection.month6)}</div>
            </div>
          </div>
          <div className="projection-assumptions">
            <span className="info-text">
              Assumes ${projection.assumptions.dailyVolume}/day volume, {(projection.assumptions.taxRate * 100).toFixed(0)}% tax reinvestment, {(projection.assumptions.tradingProfitRate * 100).toFixed(0)}% trading profit
            </span>
          </div>
        </div>
      )}

      <div className="widget-footer">
        <span className="last-updated">
          Updated {new Date(allocation.lastUpdated).toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
};
